import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dumbbell,
  Plus,
  TrendingUp,
  Activity,
  Users,
  Calendar,
  ChevronRight,
  Target,
  Zap,
  Weight,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const LIFT_COLORS = {
  SQUAT: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
  TRAPBAR: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  BENCH: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  ROW: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
  HIP_THRUST: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  CUSTOM: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400',
};

const StaffVBTPage = () => {
  const { api, hasRole } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [tests, setTests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Test creation form
  const [testForm, setTestForm] = useState({
    athlete_id: '',
    team_id: '',
    date: new Date().toISOString().split('T')[0],
    exercise_id: '',
    custom_lift_name: '',
    test_data: [
      { load_kg: '', v_mean: '', v_peak: '', reps: 1 },
      { load_kg: '', v_mean: '', v_peak: '', reps: 1 },
      { load_kg: '', v_mean: '', v_peak: '', reps: 1 },
    ],
    notes: '',
  });

  const canEdit = hasRole(['ADMIN', 'SNC']);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [api]);

  const fetchAthletes = useCallback(async () => {
    try {
      const params = selectedTeam !== 'all' ? `?team_id=${selectedTeam}` : '';
      const response = await api.get(`/athletes${params}`);
      setAthletes(response.data);
    } catch (err) {
      console.error('Failed to fetch athletes:', err);
    }
  }, [api, selectedTeam]);

  const fetchExercises = useCallback(async () => {
    try {
      const response = await api.get('/prescriptions/library?category=STRENGTH');
      setExercises(response.data);
    } catch (err) {
      console.error('Failed to fetch exercises:', err);
    }
  }, [api]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedTeam !== 'all') params.append('team_id', selectedTeam);

      const [testsRes, summaryRes] = await Promise.all([
        api.get(`/vbt/tests?${params.toString()}&limit=20`),
        api.get(`/vbt/summary?${params.toString()}`),
      ]);

      setTests(testsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to fetch VBT data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [api, selectedTeam]);

  useEffect(() => {
    fetchTeams();
    fetchExercises();
  }, [fetchTeams, fetchExercises]);

  useEffect(() => {
    fetchAthletes();
    fetchData();
  }, [fetchAthletes, fetchData]);

  const handleCreateTest = async (e) => {
    e.preventDefault();
    
    // Validate test data
    const validData = testForm.test_data.filter(
      (d) => d.load_kg && d.v_mean && d.v_peak
    );
    
    if (validData.length < 2) {
      toast.error('At least 2 valid data points required');
      return;
    }

    try {
      const payload = {
        ...testForm,
        test_data: validData.map((d) => ({
          load_kg: parseFloat(d.load_kg),
          v_mean: parseFloat(d.v_mean),
          v_peak: parseFloat(d.v_peak),
          reps: parseInt(d.reps) || 1,
        })),
      };

      await api.post('/vbt/tests', payload);
      toast.success(language === 'IT' ? 'Test VBT creato!' : 'VBT test created!');
      setShowCreateDialog(false);
      fetchData();
      
      // Reset form
      setTestForm({
        athlete_id: '',
        team_id: '',
        date: new Date().toISOString().split('T')[0],
        exercise_id: '',
        custom_lift_name: '',
        test_data: [
          { load_kg: '', v_mean: '', v_peak: '', reps: 1 },
          { load_kg: '', v_mean: '', v_peak: '', reps: 1 },
          { load_kg: '', v_mean: '', v_peak: '', reps: 1 },
        ],
        notes: '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create test');
    }
  };

  const addDataPoint = () => {
    setTestForm((prev) => ({
      ...prev,
      test_data: [...prev.test_data, { load_kg: '', v_mean: '', v_peak: '', reps: 1 }],
    }));
  };

  const updateDataPoint = (index, field, value) => {
    setTestForm((prev) => ({
      ...prev,
      test_data: prev.test_data.map((d, i) =>
        i === index ? { ...d, [field]: value } : d
      ),
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Dumbbell className="w-6 h-6 text-amber-500" />
            VBT
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {language === 'IT' ? 'Velocity-Based Training' : 'Velocity-Based Training'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800" data-testid="team-filter">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">{t('all')} {t('teams')}</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>{team.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <>
            <Link to="/staff/vbt/group-test">
              <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10" data-testid="group-test-btn">
                <Users className="w-4 h-4 mr-2" />
                {language === 'IT' ? 'Test di Gruppo' : 'Group Test'}
              </Button>
            </Link>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="create-test-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'IT' ? 'Nuovo Test' : 'New Test'}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-zinc-100">
                    {language === 'IT' ? 'Crea Test VBT' : 'Create VBT Test'}
                  </DialogTitle>
                  <DialogDescription className="text-zinc-500">
                    {language === 'IT' ? 'Registra un nuovo test load-velocity' : 'Record a new load-velocity test'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTest} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Atleta' : 'Athlete'}</Label>
                      <Select
                        value={testForm.athlete_id}
                        onValueChange={(v) => {
                          const athlete = athletes.find((a) => a.id === v);
                          setTestForm((prev) => ({
                            ...prev,
                            athlete_id: v,
                            team_id: athlete?.team_id || prev.team_id,
                          }));
                        }}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="athlete-select">
                          <SelectValue placeholder="Select athlete" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {athletes.map((athlete) => (
                            <SelectItem key={athlete.id} value={athlete.id}>
                              {athlete.first_name} {athlete.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Team' : 'Team'}</Label>
                      <Select
                        value={testForm.team_id}
                        onValueChange={(v) => setTestForm((prev) => ({ ...prev, team_id: v }))}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="team-select">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {teams.map((team) => (
                            <SelectItem key={team.id} value={team.id}>{team.code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Data' : 'Date'}</Label>
                      <Input
                        type="date"
                        value={testForm.date}
                        onChange={(e) => setTestForm((prev) => ({ ...prev, date: e.target.value }))}
                        className="bg-zinc-950 border-zinc-800"
                        data-testid="test-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'IT' ? 'Esercizio' : 'Exercise'}</Label>
                      <Select
                        value={testForm.exercise_id}
                        onValueChange={(v) => setTestForm((prev) => ({ ...prev, exercise_id: v }))}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="exercise-select">
                          <SelectValue placeholder={language === 'IT' ? 'Seleziona esercizio' : 'Select exercise'} />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {exercises.map((ex) => (
                            <SelectItem key={ex.id} value={ex.id}>
                              {ex.name} {ex.vbt_lift_type ? `(${ex.vbt_lift_type})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Test Data Points */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>{language === 'IT' ? 'Dati Test' : 'Test Data'}</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addDataPoint}
                        className="border-zinc-700"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {language === 'IT' ? 'Aggiungi' : 'Add Point'}
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <div className="grid grid-cols-4 gap-2 text-xs text-zinc-500 uppercase">
                        <div>Load (kg)</div>
                        <div>V Mean (m/s)</div>
                        <div>V Peak (m/s)</div>
                        <div>Reps</div>
                      </div>
                      {testForm.test_data.map((point, idx) => (
                        <div key={idx} className="grid grid-cols-4 gap-2">
                          <Input
                            type="number"
                            step="0.5"
                            placeholder="60"
                            value={point.load_kg}
                            onChange={(e) => updateDataPoint(idx, 'load_kg', e.target.value)}
                            className="bg-zinc-950 border-zinc-800"
                            data-testid={`load-${idx}`}
                          />
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.85"
                            value={point.v_mean}
                            onChange={(e) => updateDataPoint(idx, 'v_mean', e.target.value)}
                            className="bg-zinc-950 border-zinc-800"
                            data-testid={`vmean-${idx}`}
                          />
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="1.20"
                            value={point.v_peak}
                            onChange={(e) => updateDataPoint(idx, 'v_peak', e.target.value)}
                            className="bg-zinc-950 border-zinc-800"
                            data-testid={`vpeak-${idx}`}
                          />
                          <Input
                            type="number"
                            min="1"
                            placeholder="1"
                            value={point.reps}
                            onChange={(e) => updateDataPoint(idx, 'reps', e.target.value)}
                            className="bg-zinc-950 border-zinc-800"
                            data-testid={`reps-${idx}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{language === 'IT' ? 'Note' : 'Notes'}</Label>
                    <Textarea
                      value={testForm.notes}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, notes: e.target.value }))}
                      className="bg-zinc-950 border-zinc-800"
                      placeholder={language === 'IT' ? 'Note opzionali...' : 'Optional notes...'}
                      data-testid="test-notes"
                    />
                  </div>

                  <DialogFooter>
                    <Button type="submit" className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="submit-test">
                      {language === 'IT' ? 'Crea Test' : 'Create Test'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Activity className="w-5 h-5 text-blue-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100">{summary?.total_tests || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {language === 'IT' ? 'Test Totali' : 'Total Tests'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-emerald-400">{summary?.athletes_tested || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {language === 'IT' ? 'Atleti Testati' : 'Athletes Tested'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Zap className="w-5 h-5 text-purple-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100">{summary?.recent_tests?.length || 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">
              {language === 'IT' ? 'Test Questa Settimana' : 'Tests This Week'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tests List */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">{language === 'IT' ? 'Test Recenti' : 'Recent Tests'}</CardTitle>
          </CardHeader>
          <CardContent>
            {tests.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                {language === 'IT' ? 'Nessun test trovato' : 'No tests found'}
              </div>
            ) : (
              <div className="space-y-3">
                {tests.map((test) => (
                  <Link
                    key={test.id}
                    to={`/staff/vbt/tests/${test.id}`}
                    className={`block p-4 rounded-sm border transition-colors hover:bg-zinc-800/30 ${LIFT_COLORS[test.lift_type] || LIFT_COLORS.CUSTOM}`}
                    data-testid={`test-${test.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-zinc-100">{test.athlete_name}</div>
                        <div className="text-sm text-zinc-400 mt-1">
                          {test.lift_type}{test.custom_lift_name ? `: ${test.custom_lift_name}` : ''} • {test.team_code} • {test.date}
                        </div>
                      </div>
                      <div className="text-right">
                        {test.lv_profile && (
                          <div className="text-sm">
                            <span className="text-zinc-500">L0:</span>{' '}
                            <span className="font-medium text-zinc-100">{typeof test.lv_profile.l0 === 'number' ? test.lv_profile.l0.toFixed(1) : test.lv_profile.l0} kg</span>
                          </div>
                        )}
                        {test.quality_report && (
                          <div className={`text-xs mt-1 ${test.quality_report.is_valid ? 'text-emerald-400' : 'text-amber-400'}`}>
                            R² = {test.lv_profile?.r_squared?.toFixed(2) || 'N/A'}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      {/* Quick Links */}
      <div className="flex flex-wrap gap-4">
        {athletes.slice(0, 5).map((athlete) => (
          <Link
            key={athlete.id}
            to={`/staff/vbt/athlete/${athlete.id}`}
            className="text-sm text-zinc-500 hover:text-amber-500 transition-colors flex items-center gap-1"
          >
            {athlete.first_name} {athlete.last_name} <ChevronRight className="w-3 h-3" />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default StaffVBTPage;
